// src/components/import/AdvancedImportPanel.tsx
import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { parseFriendlyNotes } from "../../lib/friendly-parse";
import { toCueNotes } from "../../lib/typesafe";
import { CueNote } from "../../types";

type Props = {
  onProcessNotes: (notes: CueNote[]) => void;
  featureMode?: "hidden" | "collapsed" | "enabled";
};

type Mode = "friendly" | "json";

const detectMode = (): "hidden" | "collapsed" | "enabled" => {
  try {
    const { env } = (import.meta as any);
    return (env?.VITE_FEATURE_FRIENDLY_PASTE as any) ?? "hidden";
  } catch {
    return "hidden";
  }
};

export default function AdvancedImportPanel({ onProcessNotes, featureMode }: Props) {
  const FP_MODE = useMemo(() => featureMode ?? detectMode(), [featureMode]);
  const show = useMemo(() => FP_MODE !== "hidden", [FP_MODE]);

  const [mode, setMode] = useState<Mode>("friendly");
  const [raw, setRaw] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (!show) return null;

  return (
    <div className="card hoverable vstack-md" style={{ marginTop: 16, padding: 0 }}>
      <button
        type="button"
        className="button button-secondary"
        style={{
          width: "100%",
          justifyContent: "space-between",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          padding: "12px 16px",
        }}
        onClick={() => setMode((m) => (m === "friendly" ? "json" : "friendly"))}
        aria-controls="advanced-content"
        data-no-rowselect
      >
        <span className="small" style={{ fontWeight: 600, color: "#344054" }}>
          Friendly Paste (Power users)
        </span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>

      <div id="advanced-content" className="vstack-sm" style={{ borderTop: "1px solid #e5e7eb", padding: 16 }}>
        {mode === "friendly" ? (
          <>
            <p className="muted small">Paste in the Friendly format. (We generally recommend Prep Kits.)</p>
            <label htmlFor="friendly-textarea" className="label">
              Friendly input
            </label>
            <textarea
              id="friendly-textarea"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={`CueBoard #: Header
Notes:
- Bullet 1
- Bullet 2
- Bullet 3
Triggers: Trigger 1; Trigger 2; Trigger 3`}
              rows={10}
              className="textarea-main"
            />
          </>
        ) : (
          <>
            <p className="muted small">Paste a JSON CueNote array, or use file upload below.</p>
            <label htmlFor="json-textarea" className="label">
              JSON input
            </label>
            <textarea
              id="json-textarea"
              className="textarea-main h-64"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder='[{"id":"elevator_pitch","title":"Elevator Pitch","triggers":["tell me about yourself"],"body_markdown":"- Bullet 1\n- Bullet 2"}]'
            />
          </>
        )}

        <div className="actions">
          <button
            className="button button-primary"
            onClick={() => {
              try {
                setErr(null);
                if (mode === "friendly") {
                  const { notes, errors } = parseFriendlyNotes(raw);
                  if (errors.length) return alert(errors.join("; "));
                  if (notes.length === 0) return alert("Provide at least one cue");
                  onProcessNotes(notes);
                } else {
                  const parsed = JSON.parse(raw) as unknown;
                  const arr = toCueNotes(parsed);
                  if (!arr) return alert("Provide a CueNote or an array of CueNotes");
                  onProcessNotes(arr as CueNote[]);
                }
              } catch (e: any) {
                const msg = e?.message || "Invalid input";
                setErr(msg);
                alert(msg);
              }
            }}
          >
            Save and Continue
          </button>
        </div>

        {err && <div className="small" style={{ color: "#e11d48" }}>{err}</div>}
      </div>
    </div>
  );
}
